import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Promotion } from '../types';
import { WorkspaceScope, PublicInactiveNotice } from '../types/workspace';
import { SystemSettings } from '../types/systemSettings';
import { WorkspaceApiError } from '../services/workspaceApi';
import { loadPublicPromotion } from '../services/publicPromotionService';
import InactivePublicContent from '../components/public/InactivePublicContent';
import { recordPromotionView } from '../services/promotionStatsService';
import { WorkspaceProvider } from '../components/common/WorkspaceProvider';
import PromotionPublicContent from '../components/public/PromotionPublicContent';
import { PromotionThemeProvider } from '../theme/PromotionTheme';
import { Alert, AlertDescription } from '../components/ui/alert';
import CharacterImage from '../components/common/CharacterImage';
import { CharacterTheme } from '../config/characterThemeAssets.mjs';

import { buildSharingMetadata } from '../config/sharingPolicy.mjs';
import PublicSharingMetadata from '../components/public/PublicSharingMetadata';

const PromotionViewPage: React.FC = () => {
  const { identifier } = useParams<{ identifier: string }>();
  const [searchParams] = useSearchParams();
  const [promotion, setPromotion] = useState<Promotion | null>(null);
  const [scope, setScope] = useState<WorkspaceScope | null>(null);
  const [systemSettings, setSystemSettings] = useState<SystemSettings | null>(null);
  const [inactiveNotice, setInactiveNotice] = useState<PublicInactiveNotice | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<{ message: string; retryable: boolean } | null>(null);
  const [retryAttempt, setRetryAttempt] = useState(0);
  const [errorTheme, setErrorTheme] = useState<CharacterTheme>('male');
  const recorded = useRef<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    setPromotion(null); setScope(null); setSystemSettings(null); setError(null); setIsLoading(true);
    setInactiveNotice(null); setErrorTheme('male');
    loadPublicPromotion(identifier || '')
      .then(result => {
        if (cancelled) return;
        setScope(result.scope); setPromotion(result.promotion); setSystemSettings(result.settings);
        if (result.promotion.isActive && recorded.current !== identifier) {
          recorded.current = identifier || null;
          void recordPromotionView(result.scope, result.promotion.id);
        }
      }).catch(failure => {
        if (cancelled) return;
        if (failure instanceof WorkspaceApiError) setErrorTheme(failure.characterTheme || 'male');
        if (failure instanceof WorkspaceApiError && failure.notice) {
          setInactiveNotice(failure.notice);
          const inactive = failure.notice.promotion;
          if (failure.status === 404 && inactive?.id && inactive.slug === identifier && recorded.current !== identifier) {
            recorded.current = identifier || null;
            // 종료 본문은 공개하지 않고 유효한 공유 링크 접속만 기록한다.
            void recordPromotionView({ mode: 'public', workspaceId: '', accountCode: '', publicIdentifier: identifier }, inactive.id);
          }
        }
        else if (failure instanceof WorkspaceApiError && failure.status === 404) {
          setError({ message: '공개 중인 프로모션을 찾을 수 없습니다. 링크와 계정 상태를 확인해주세요.', retryable: false });
        } else {
          setError({ message: '프로모션 정보를 불러오지 못했습니다. 연결을 확인한 뒤 다시 시도해주세요.', retryable: true });
        }
      })
      .finally(() => { if (!cancelled) setIsLoading(false); });
    return () => { cancelled = true; };
  }, [identifier, retryAttempt]);
  if (isLoading) {
    return (
      <>
        <Helmet>
          <title>로딩 중... - 렌탈프로모션</title>
        </Helmet>
        <div className="promotion-page-state" role="status" aria-live="polite">
          <div className="text-center">
            <div className="promotion-spinner mb-4"></div>
            <p className="promotion-muted">프로모션 정보를 불러오고 있습니다.</p>
          </div>
        </div>
      </>
    );
  }

  if (inactiveNotice) return <div><PublicSharingMetadata metadata={buildSharingMetadata({ origin: window.location.origin, identifier, settings: inactiveNotice, state: 'inactive' })} /><InactivePublicContent notice={inactiveNotice} identifier={identifier || ''} /></div>;

  if (error || !promotion) {
    return (
      <>
        <PublicSharingMetadata metadata={buildSharingMetadata({ origin: window.location.origin, identifier, state: 'error' })} />
        <div className="promotion-page-state">
          <div className="max-w-md w-full">
            {!error?.retryable && <div className="text-center mb-4">
              <CharacterImage theme={errorTheme} slot="linkError"
                alt="링크 오류"
                className="mx-auto mb-4 max-w-[70%] h-auto"
              />
            </div>}
            <Alert variant="destructive">
              <AlertDescription className="text-lg">
                {error?.message || '프로모션을 찾을 수 없습니다.\n링크가 완전하지 않은 것 같으니, 문자를 받으셨다면 "모두보기"를 클릭하여 전체 링크가 나온 상태에서 클릭을 다시 해보세요'}
              </AlertDescription>
            </Alert>
            {error?.retryable && <button type="button" className="promotion-size-button mt-4" onClick={() => setRetryAttempt(attempt => attempt + 1)}>다시 시도</button>}
          </div>
        </div>
      </>
    );
  }

  // URL 파라미터에서 hideElements 값을 가져옴
  const hideElements = searchParams.get('hideElements');

  return (
    <>
      <PublicSharingMetadata metadata={buildSharingMetadata({ origin: window.location.origin, identifier, settings: systemSettings, promotion })} />
      <WorkspaceProvider key={identifier} scope={scope!}><PromotionPublicContent promotion={promotion} hideElements={hideElements} systemSettings={systemSettings} /></WorkspaceProvider>
    </>
  );
};

export default function ThemedPromotionViewPage() {
  return <PromotionThemeProvider><PromotionViewPage /></PromotionThemeProvider>;
}
